<template>
  <QuoteBesoinDialog
    ref="dialogRef"
    :model-value="open"
    :form="form"
    @update:model-value="onDialogUpdate"
    @submit="handleSubmit"
  />
</template>

<script setup>
/**
 * HÔTE DE LA FENÊTRE « ANALYSE DU BESOIN » — il porte l'état, elle le rend.
 *
 * IL N'EXISTE QUE PENDANT L'OUVERTURE, et c'est toute sa raison d'être :
 * `useQuoteBesoinForm` monte `useQuoteDetailPage`, qui charge la demande, ses
 * commentaires, son historique et ses doublons AU MONTAGE. La fenêtre, elle,
 * est montée une fois pour toutes dans App.vue — le composable posé à ce
 * niveau-là aurait tiré une demande à chaque démarrage de l'application, sans
 * identifiant et sans que personne ne regarde. L'aiguilleur monte donc cet hôte
 * sous `v-if`, et le démonte à la fermeture.
 *
 * C'est aussi lui le propriétaire des effets de bord que M10 interdit à la
 * fenêtre : le chargement, et le toast de succès.
 */
import { nextTick, onMounted, ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import QuoteBesoinDialog from './QuoteBesoinDialog.vue'
import { useQuoteBesoinForm } from '../../composables/quotes/useQuoteBesoinForm.js'

const props = defineProps({
  quoteId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['close'])

// Valeur brute et non ref : `useQuoteDetailPage` la consomme telle quelle.
// L'hôte est remonté à chaque changement de demande (`:key`), la constante suit.
const form = useQuoteBesoinForm(String(props.quoteId), { preview: false })

// Le panneau est monté à `false` puis ouvert au tick suivant : sans ce
// décalage, `Dialog` naîtrait déjà ouvert et sa transition d'entrée ne
// jouerait pas — la fenêtre apparaîtrait d'un bloc.
const open = ref(false)
const dialogRef = ref(null)

// LA GARDE SE POSE QUAND TOUT EST RELU — la demande ET la validation, comme la
// page pleine page. La fenêtre la posait à la fin du seul chargement de la
// demande : la validation arrivée ensuite (cases restaurées, section décochée
// d'office faute d'une réponse obligatoire) la faisait naître « modifiée », et
// fermer sans rien toucher demandait d'abandonner une saisie inexistante.
//
// Les questionnaires de la formation aussi : ils arrivent après la demande, et
// une question à réponse unique laissée vide décoche « Formation » à leur
// arrivée — prise avant, la référence faisait encore naître la fenêtre « modifiée ».
//
// On guette la demande elle-même et son passage en édition, pas l'indicateur
// `loading` : il retombe AVANT que la copie d'édition ne soit posée, et le
// point de référence était pris sur une copie encore vide (tracé au navigateur).
const validationChargee = ref(false)
watch(
  () => [form.quote.value, form.isEditing.value, validationChargee.value, form.questionnairesCharges.value],
  async ([quote, , chargee, questionnaires]) => {
    if (!quote || !chargee || !questionnaires) return
    await nextTick()
    dialogRef.value?.baseline()
  }
)

onMounted(async () => {
  await nextTick()
  open.value = true
  await form.load()
  validationChargee.value = true
})

function onDialogUpdate(value) {
  if (!value) {
    open.value = false
    emit('close')
  }
}

async function handleSubmit() {
  const ok = await form.submit()
  // Le succès se dit en toast ; l'échec, lui, reste DANS la fenêtre sur le
  // formulaire encore rempli (M9) — c'est `form.error` qui le porte.
  if (ok) {
    toast.success('Analyse du besoin enregistrée')
    open.value = false
    emit('close')
  }
}
</script>
